import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Scale, AlertTriangle, CheckCircle2, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Side Imbalance Watch — averages per-side e1RM across unilateral exercises over the
 * last 4 weeks and flags any exercise where one side is ≥10% behind.
 * Hidden when the user has no unilateral data yet.
 */
export default function ImbalanceCard() {
    const [data, setData] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        api.get("/progress/imbalance")
            .then(r => setData(r.data))
            .catch(() => setError(true));
    }, []);

    if (error) return null;
    if (!data) {
        return (
            <div className="tactical-card p-6" data-testid="imbalance-loading">
                <div className="label-eyebrow text-muted-foreground">Side Imbalance Watch</div>
                <p className="mt-2 text-sm text-muted-foreground">Crunching the last 4 weeks…</p>
            </div>
        );
    }

    // Hide entirely when there's nothing unilateral to watch
    if (data.summary.tracked_exercises === 0) return null;

    const flagged = data.exercises.filter(e => e.flagged);
    const allGood = flagged.length === 0;

    return (
        <div className="tactical-card p-6" data-testid="imbalance-card">
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 label-eyebrow text-accent-red">
                        <Scale className="h-3.5 w-3.5" /> Side Imbalance Watch
                    </div>
                    <h2 className="mt-1 font-display text-2xl font-black uppercase leading-tight sm:text-3xl">
                        {allGood ? "You're balanced." : `${flagged.length} side gap${flagged.length > 1 ? "s" : ""} to close.`}
                    </h2>
                    <p className="mt-1 text-xs text-muted-foreground">
                        Combined weight + reps gap (Epley e1RM) across {data.summary.tracked_exercises} unilateral exercise{data.summary.tracked_exercises === 1 ? "" : "s"} over the last {data.window_days} days:
                        <span className={`ml-1 font-display text-sm font-black ${data.summary.overall_gap_pct >= 10 ? "text-accent-red" : "text-foreground"}`} data-testid="imbalance-overall-gap">{data.summary.overall_gap_pct}%</span>
                    </p>
                </div>
                <div className="flex-none">
                    {allGood
                        ? <CheckCircle2 className="h-8 w-8 text-accent-red/80" data-testid="imbalance-all-good" />
                        : <AlertTriangle className="h-8 w-8 text-accent-red" data-testid="imbalance-warning" />}
                </div>
            </div>

            <ul className="mt-5 space-y-2">
                {data.exercises.slice(0, 4).map(ex => (
                    <li key={ex.exercise_id} className={`flex items-center gap-3 border bg-card/60 p-3 ${ex.flagged ? "border-accent-red/60" : "border-border"}`} data-testid={`imbalance-row-${ex.exercise_id}`}>
                        <div className="min-w-0 flex-1">
                            <div className="truncate font-display text-base font-black uppercase">{ex.exercise_name}</div>
                            <div className="mt-1 grid grid-cols-2 gap-2 text-[11px] sm:max-w-md">
                                <div className="border-l-2 border-foreground/30 pl-2">
                                    <div className="label-eyebrow text-muted-foreground">Left</div>
                                    <div className="mt-0.5 font-bold tabular-nums text-foreground/90">
                                        {ex.left_avg_weight}{" "}lb × {ex.left_avg_reps} reps
                                    </div>
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">e1RM {ex.left_avg_e1rm}</div>
                                </div>
                                <div className={`border-l-2 pl-2 ${ex.dominant_side === "right" ? "border-accent-red" : "border-foreground/30"}`}>
                                    <div className="label-eyebrow text-muted-foreground">Right</div>
                                    <div className="mt-0.5 font-bold tabular-nums text-foreground/90">
                                        {ex.right_avg_weight} lb × {ex.right_avg_reps} reps
                                    </div>
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">e1RM {ex.right_avg_e1rm}</div>
                                </div>
                            </div>
                            <div className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                                {ex.set_count} sets · weight gap {ex.weight_gap_pct}% · reps gap {ex.reps_gap_pct}%
                            </div>
                        </div>
                        <Bar gap={ex.gap_pct} dominant={ex.dominant_side} flagged={ex.flagged} />
                        <div className={`min-w-[58px] text-right font-display text-xl font-black tabular-nums ${ex.flagged ? "text-accent-red" : "text-foreground"}`} data-testid={`imbalance-gap-${ex.exercise_id}`}>
                            {ex.gap_pct}%
                        </div>
                    </li>
                ))}
            </ul>

            {flagged.length > 0 && (
                <div className="mt-4 border-l-2 border-accent-red bg-accent-red/8 px-4 py-3 text-xs text-foreground/80">
                    <span className="font-bold uppercase tracking-widest text-accent-red">Heads up · </span>
                    Your <span className="font-bold uppercase">{flagged[0].lagging_side}</span> side is lagging on <span className="font-bold">{flagged[0].exercise_name}</span>. Next session, start with the weaker side and match weight/reps on the stronger side — let the lagging side set the pace.
                </div>
            )}

            <Link to="/progress" data-testid="imbalance-view-progress" className="mt-5 inline-flex items-center gap-1 label-eyebrow text-muted-foreground hover:text-accent-red">
                See full progress <ChevronRight className="h-3 w-3" />
            </Link>
        </div>
    );
}

function Bar({ gap, dominant, flagged }) {
    // Visual: split bar with the dominant side filled, lagging side dimmer.
    const dominantPct = 50 + Math.min(gap, 40) / 2;
    const lagPct = 100 - dominantPct;
    const isRightDominant = dominant === "right";
    const leftWidth = isRightDominant ? lagPct : dominantPct;
    const rightWidth = isRightDominant ? dominantPct : lagPct;
    const accent = flagged ? "bg-accent-red" : "bg-foreground/55";
    const dim = "bg-border";
    return (
        <div className="hidden h-7 w-32 flex-none overflow-hidden border border-border sm:flex">
            <div className={`${isRightDominant ? dim : accent} h-full transition-all`} style={{ width: `${leftWidth}%` }} title={`L ${leftWidth.toFixed(0)}%`} />
            <div className={`${isRightDominant ? accent : dim} h-full transition-all`} style={{ width: `${rightWidth}%` }} title={`R ${rightWidth.toFixed(0)}%`} />
        </div>
    );
}
